import {
  Button,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  RadioGroup,
  RadioGroupItem,
} from '@ibsys/kit';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ExpirationType, ServiceTokenFormValues } from './types';
import { useServiceTokenSchema } from './validation';

type Props = {
  onCancel: () => void;
  onSubmit: (data: { description: string; expiredAt?: string }) => void;
  isLoading?: boolean;
};

// Get default expiration date (1 year from now) in datetime-local format
const getDefaultExpirationDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  // Format: YYYY-MM-DDTHH:mm
  return date.toISOString().slice(0, 16);
};

export const CreateServiceTokenDialog: FC<Props> = ({ onCancel, onSubmit, isLoading }) => {
  const { t } = useTranslation(['users', 'common']);
  const [expirationType, setExpirationType] = useState<ExpirationType>('unlimited');
  const schema = useServiceTokenSchema(expirationType);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ServiceTokenFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      description: '',
      expiredAt: getDefaultExpirationDate(),
    },
  });

  const handleFormSubmit = (data: ServiceTokenFormValues) => {
    onSubmit({
      description: data.description,
      expiredAt: expirationType === 'date' && data.expiredAt ? data.expiredAt : undefined,
    });
  };

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>{t('users:createServiceToken')}</DialogTitle>
      </DialogHeader>

      <form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col gap-4">
        <Input
          control={control}
          name="description"
          label={t('users:tokenDescription')}
          placeholder={t('users:tokenDescriptionPlaceholder')}
          required
          error={errors.description?.message}
        />

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t('users:tokenExpiration')}</label>
          <RadioGroup
            value={expirationType}
            onChange={(value: ExpirationType) => setExpirationType(value)}
            className="flex flex-col gap-2"
          >
            <RadioGroupItem value="unlimited" className="flex items-center gap-2">
              <span>{t('users:unlimited')}</span>
            </RadioGroupItem>
            <RadioGroupItem value="date" className="flex items-center gap-2">
              <span>{t('users:expiresAt')}</span>
            </RadioGroupItem>
          </RadioGroup>

          {expirationType === 'date' && (
            <Input
              control={control}
              name="expiredAt"
              type="datetime-local"
              error={errors.expiredAt?.message}
              className="mt-2"
            />
          )}
        </div>

        <DialogFooter>
          <div className="flex gap-3 justify-end w-full">
            <Button variant="ghost" onClick={onCancel} type="button">
              {t('common:cancel')}
            </Button>
            <Button type="submit" isLoading={isLoading}>
              {t('users:createToken')}
            </Button>
          </div>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};
